import { vars } from "@frontend/styles";

import type { CardGlow } from "./Card.types";

export type GlowPointerState = {
  x: number;
  y: number;
  color: string;
  size: number;
};

type GlowRenderer = (state: GlowPointerState) => string;

// Angle from the card's center to the pointer
function pointerAngleRad({ x, y }: GlowPointerState): number {
  return Math.atan2(y - 0.5, x - 0.5);
}

const RENDERERS: Record<CardGlow, GlowRenderer> = {
  border: ({ x, y, color, size }) =>
    `radial-gradient(${size}px circle at ${x * 100}% ${y * 100}%, ${color}, transparent 70%)`,
  pulse: ({ x, y, color, size }) =>
    `radial-gradient(${size}px circle at ${x * 100}% ${y * 100}%, ${color}, transparent 70%)`,
  background: ({ x, y, color, size }) =>
    `radial-gradient(${size}px circle at ${x * 100}% ${y * 100}%, ${color}, transparent 70%)`,
  spotlight: ({ x, y, size }) =>
    `radial-gradient(${size * 0.6}px circle at ${x * 100}% ${y * 100}%, transparent 0%, transparent 25%, ${vars.color.scrim} 100%)`,
  "glow-line": (state) =>
    `conic-gradient(from ${pointerAngleRad(state)}rad, transparent 0%, ${state.color} 10%, transparent 20%)`,
  rainbow: (state) =>
    `conic-gradient(from ${pointerAngleRad(state)}rad at ${state.x * 100}% ${state.y * 100}%, #f06, #9f0, #0ff, #90f, #f06)`,
};

const OPACITY_SCALE: Partial<Record<CardGlow, number>> = {
  background: 0.15,
};

export function glowOpacity(
  glow: CardGlow,
  base: number,
  intensity: number,
): number {
  return base * intensity * (OPACITY_SCALE[glow] ?? 1);
}

export function renderGlowBackground(
  glow: CardGlow,
  state: GlowPointerState,
): string {
  return RENDERERS[glow](state);
}

const BORDER_BOX_GLOWS = new Set<CardGlow>([
  "border",
  "glow-line",
  "rainbow",
  "pulse",
]);

export function paintGlowElement(
  el: HTMLElement,
  glow: CardGlow,
  state: GlowPointerState,
): void {
  el.style.background = renderGlowBackground(glow, state);
  el.style.backgroundOrigin = BORDER_BOX_GLOWS.has(glow) ? "border-box" : "";
}
